import { useEffect, useRef, useState } from "react";
import { cn } from "tailwind-variants";
import type {
	AlertUmbBlockElementModel,
	AlertUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { useAutoplayActivity } from "#/_packages/umbrella/features/carousel/components/use-autoplay-activity";
import { useGetSiteSettings } from "#/_packages/umbrella/hooks/useGetSiteSettings";
import AlertUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/alert-block";
import { shouldRenderBlockGridItem } from "#/_packages/umbrella/integrations/umbraco/utils/block-visibility";
import { m } from "#/paraglide/messages";

function readDismissedAlerts(): { id: string; alertUpdated?: string }[] {
	try {
		const value = JSON.parse(
			localStorage.getItem("dismissedAlerts") ?? "null",
		)?.value;
		return Array.isArray(value)
			? value.filter((item) => typeof item?.id === "string")
			: [];
	} catch {
		return [];
	}
}

/** Site-wide alerts render before the page frame, including pages without a header. */
export function GlobalAlerts() {
	const settings = useGetSiteSettings();
	const [dismissed, setDismissed] = useState<string[]>([]);
	const [index, setIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	useEffect(() => {
		setDismissed(
			readDismissedAlerts().map((item) => item.id.replace(/^alert-/, "")),
		);
	}, []);
	const dismiss = (id: string) => {
		setDismissed((ids) => [...ids, id]);
		try {
			const value = readDismissedAlerts().filter(
				(item) => item.id !== `alert-${id}`,
			);
			value.push({ id: `alert-${id}`, alertUpdated: new Date().toISOString() });
			localStorage.setItem("dismissedAlerts", JSON.stringify({ value }));
		} catch {
			// Dismissal still works in this session when browser storage is unavailable.
		}
	};
	const alerts = (settings?.alert?.items ?? []).filter(
		(item) =>
			item.content.contentType === "alertUmbBlock" &&
			shouldRenderBlockGridItem(item) &&
			(!(item.content as AlertUmbBlockElementModel).properties?.closeButton ||
				!dismissed.includes(item.content.id)),
	);
	const carousel = settings?.carouselAlert === true && alerts.length > 1;
	const active = index % Math.max(1, alerts.length);
	// Reduced motion disables the fade in CSS, while all service messages still rotate.
	const autoplay = useAutoplayActivity(root, carousel, false) && !paused;
	useEffect(() => {
		if (!autoplay) return;
		const timer = window.setInterval(
			() => setIndex((value) => value + 1),
			5000,
		);
		return () => window.clearInterval(timer);
	}, [autoplay]);
	if (alerts.length === 0) return null;
	return (
		// biome-ignore lint/a11y/useSemanticElements: This group contains site alerts, not form fields.
		<div
			ref={root}
			id={carousel ? "global-alert-carousel" : undefined}
			className={cn(
				!settings?.fullWidthAlert && "container",
				carousel && "carousel slide carousel-fade alert-global-carousel grid",
			)}
			role="group"
			aria-label={carousel ? m.carousel_label() : undefined}
			aria-roledescription={carousel ? "carousel" : undefined}
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
			onFocusCapture={() => setPaused(true)}
			onBlurCapture={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget))
					setPaused(false);
			}}
		>
			{alerts.map((item, position) => (
				<div
					key={item.content.id}
					aria-hidden={carousel && position !== active ? true : undefined}
					inert={carousel && position !== active}
					className={
						carousel
							? "global-alert-slide col-start-1 row-start-1 transition-opacity duration-700 motion-reduce:transition-none"
							: undefined
					}
					style={
						carousel
							? {
									opacity: position === active ? 1 : 0,
									zIndex: position === active ? 1 : 0,
									pointerEvents: position === active ? undefined : "none",
								}
							: undefined
					}
				>
					<AlertUmbBlock
						content={item.content as AlertUmbBlockElementModel}
						settings={
							item.settings as AlertUmbBlockSettingsElementModel | undefined
						}
						fullWidth={settings?.fullWidthAlert ?? false}
						className="global-alert alert-global"
						dismissLabel={m.common_close()}
						onDismiss={() => dismiss(item.content.id)}
					/>
				</div>
			))}
		</div>
	);
}
